"use client";

import { useCallback } from "react";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import { Dialog } from "@ui/components/core/DialogRedesign";
import { useDeepLinkModal } from "@ui/hooks/useDeepLinkModal";
import type { ResolvedFlowTemplate } from "@/app/_api/flowTemplatesServer";
import { CardFlowTemplate } from "./CardFlowTemplate";
import { FlowTemplateRunPanel } from "./FlowTemplateRunPanel";

interface BlockFlowTemplateGalleryProps {
  items: ResolvedFlowTemplate[];
}

// Query param used to deep-link a specific template's modal. A query param (not a
// path segment) is required because the gallery lives on CMS catch-all pages —
// extra path segments would be parsed as another CMS slug and 404.
const TEMPLATE_QUERY_PARAM = "template";

function buildUrlWithTemplate(templateId: string | null) {
  const url = new URL(window.location.href);
  if (templateId) {
    url.searchParams.set(TEMPLATE_QUERY_PARAM, templateId);
  } else {
    url.searchParams.delete(TEMPLATE_QUERY_PARAM);
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

const buildClosedUrl = () => buildUrlWithTemplate(null);

export function BlockFlowTemplateGallery({ items }: BlockFlowTemplateGalleryProps) {
  const t = useTranslations("ClientKeys.flow_template_gallery");

  // `?template=` is deep-linkable, so the modal opens on mount and re-derives on
  // back/forward; a deep-linked close strips the param in place.
  const getIdFromUrl = useCallback(() => {
    const id = new URLSearchParams(window.location.search).get(TEMPLATE_QUERY_PARAM);
    return id && items.some(item => item.id === id) ? id : null;
  }, [items]);

  const {
    selectedId,
    isOpen,
    open: openTemplate,
    close: closeModal,
  } = useDeepLinkModal({
    getUrlForId: buildUrlWithTemplate,
    getIdFromUrl,
    getClosedUrl: buildClosedUrl,
  });

  const selectedItem = selectedId ? (items.find(item => item.id === selectedId) ?? null) : null;

  return (
    <div className='tw-w-full'>
      <div className='tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3'>
        {items.map(item => (
          <CardFlowTemplate key={item.id} item={item} onSelect={openTemplate} />
        ))}
      </div>

      {selectedItem && (
        <Dialog
          placement='center'
          isOpen={isOpen}
          onOpenChange={open => {
            if (!open) closeModal();
          }}
          isDismissable
          title={selectedItem.flowTemplate.name ?? t("modal_title_fallback")}
          width={920}
        >
          {/* The Dialog renders its title flush against these children, so the
              header-to-panel spacing lives here: a smaller gap to the description
              (when xi provides one) then the run panel, otherwise a single gap
              straight to it. */}
          <div
            className={clsx(
              "tw-flex tw-flex-col tw-gap-6",
              selectedItem.flowTemplate.description ? "tw-mt-3" : "tw-mt-6",
            )}
          >
            {selectedItem.flowTemplate.description && (
              <p className='tw-type-base tw-text-gray-500'>{selectedItem.flowTemplate.description}</p>
            )}
            {/* key remounts the run panel per template so switching cards starts fresh. */}
            <FlowTemplateRunPanel
              key={selectedItem.id}
              flowTemplate={selectedItem.flowTemplate}
              flowTemplateExample={selectedItem.flowTemplateExample}
              resultType={selectedItem.resultType}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
}
